import type { Shop } from "@/stores/auth";

/**
 * Which SP-API backed displays a vendor (1P) shop can ever show (#679).
 *
 * Until the Vendor SP-API authorization lands (P1, #730) a vendor shop has no
 * Seller Central data at all. Every display that depends on it must say why it is
 * empty instead of showing a blank or a skeleton, and must never send the user to
 * authorize Seller Central (vendors authorize in Vendor Central).
 *
 * - `pending_vendor_central`: Vendor Central (SP-API) can supply it later — the
 *   vendor sales / real-time sales / inventory reports or Data Kiosk
 *   `vendorAnalytics` (ordered revenue and units, sellable on-hand inventory,
 *   open purchase orders, `lostFeaturedOffer` for Buy Box).
 * - `never`: Amazon exposes no vendor equivalent (sessions and anything derived
 *   from them); hide the display instead of promising it.
 *
 * The Product Center (#675) reuses this table and the `common:vendorData` copy.
 */
export type VendorMetricAvailability = "available" | "pending_vendor_central" | "never";

export type SpApiMetric =
  | "totalSales"
  | "realtimeSales"
  | "totalOrders"
  | "tacos"
  | "organicSales"
  | "organicOrders"
  | "organicTraffic"
  | "inventory"
  | "buyBox"
  | "sessions"
  | "sessionConversion";

const VENDOR_AVAILABILITY: Record<SpApiMetric, Exclude<VendorMetricAvailability, "available">> = {
  totalSales: "pending_vendor_central",
  realtimeSales: "pending_vendor_central",
  totalOrders: "pending_vendor_central",
  tacos: "pending_vendor_central",
  organicSales: "pending_vendor_central",
  organicOrders: "pending_vendor_central",
  // The vendor traffic report only has glance views; sessions never exist.
  organicTraffic: "pending_vendor_central",
  inventory: "pending_vendor_central",
  buyBox: "pending_vendor_central",
  sessions: "never",
  sessionConversion: "never",
};

/** Whether a dashboard / product metric key is one of the SP-API backed metrics above. */
export function isSpApiMetric(metric: string): metric is SpApiMetric {
  return Object.hasOwn(VENDOR_AVAILABILITY, metric);
}

export type AccountType = Shop["accountType"];

/** Availability of one SP-API backed metric for one shop; unknown account types read as seller. */
export function spApiMetricAvailability(
  accountType: AccountType,
  metric: SpApiMetric,
): VendorMetricAvailability {
  return accountType === "vendor" ? VENDOR_AVAILABILITY[metric] : "available";
}

/** Account mix of an analytics scope: every shop a vendor, none, or both kinds. */
export type ScopeAccountMix = "seller" | "vendor" | "mixed";

export type ScopeShop = Pick<Shop, "shopId" | "accountType">;

export interface ScopeAccountSplit<T extends ScopeShop = Shop> {
  mix: ScopeAccountMix;
  /** Shops that have (or can have) Seller Central data, in scope order. */
  sellers: T[];
  /** Vendor (1P) shops, which have no SP-API data until Vendor Central lands. */
  vendors: T[];
}

export function splitScopeByAccountType<T extends ScopeShop>(
  shops: readonly T[],
  shopIds: readonly string[],
): ScopeAccountSplit<T> {
  const selected = shops.filter((shop) => shopIds.includes(shop.shopId));
  const vendors = selected.filter((shop) => shop.accountType === "vendor");
  const sellers = selected.filter((shop) => shop.accountType !== "vendor");
  const mix: ScopeAccountMix =
    vendors.length === 0 ? "seller" : sellers.length === 0 ? "vendor" : "mixed";
  return { mix, sellers, vendors };
}

/**
 * Availability of a metric for a whole scope. A mixed scope still shows the
 * metric (computed from the seller shops only); the caller notes the coverage.
 */
export function scopeMetricAvailability(
  mix: ScopeAccountMix,
  metric: SpApiMetric,
): VendorMetricAvailability {
  return mix === "vendor" ? VENDOR_AVAILABILITY[metric] : "available";
}
